import {
    Fragment,
    type Key,
    type ReactNode,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import Flex from 'ui/Flex/Flex';

type OverflowChipListProps<T> = {
    items: T[];
    renderChip: (item: T, index: number) => ReactNode;
    renderOverflow: (hiddenItems: T[]) => ReactNode;
    getKey: (item: T, index: number) => Key;
    gap?: number;
};

/**
 * Displays chips on a single line and collapses the overflow into a counter
 * chip ("+X").
 */
const OverflowChipList = <T,>({
    items,
    renderChip,
    renderOverflow,
    getKey,
    gap = 0.5,
}: OverflowChipListProps<T>): JSX.Element => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(items.length);
    const [availableWidth, setAvailableWidth] = useState<number>();

    // Measure-then-fix before paint (hence useLayoutEffect, not useEffect, else it flickers):
    // while it overflows we drop one chip and re-measure until it fits (no fragile width estimates).
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el || visibleCount <= 1) return;
        if (el.scrollWidth > el.clientWidth) {
            setVisibleCount((count) => count - 1);
        }
    }, [visibleCount, availableWidth]);

    // AgGrid doesn't re-render the React cell on resize, so we watch the DOM width via ResizeObserver.
    // Reset to all chips; the new width re-triggers the loop above (which then re-collapses).
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !globalThis.ResizeObserver) return undefined;
        const observer = new ResizeObserver(([entry]) => {
            setVisibleCount(items.length);
            setAvailableWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [items.length]);

    const hiddenItems = items.slice(visibleCount);

    return (
        <Flex
            ref={containerRef}
            align="center"
            flex={1}
            minWidth={0}
            overflow="hidden"
            gap={gap}
        >
            {items.slice(0, visibleCount).map((item, index) => (
                <Fragment key={getKey(item, index)}>
                    {renderChip(item, index)}
                </Fragment>
            ))}
            {hiddenItems.length > 0 && renderOverflow(hiddenItems)}
        </Flex>
    );
};

export default OverflowChipList;
